import { useQuery } from '@tanstack/react-query'
import { swimApi } from '@swim/api-client'
import { QUERY_KEYS } from '@/lib/queryKeys'
import { useDebouncedValue } from './use-debounced-value'
import type { OfferResult, StreamResult } from '@/types/search'

interface SearchSuggestions {
  offers: OfferResult[]
  streams: StreamResult[]
}

const DEBOUNCE_MS = 300
const SUGGESTION_LIMIT = 5
const EMPTY_SUGGESTIONS: SearchSuggestions = { offers: [], streams: [] }

/**
 * Live, debounced catalog suggestions for the cmdk search dropdown (spec-022, US5).
 * Debounces the input value to avoid sending a request on every keystroke.
 * TanStack Query's per-key cache isolation prevents stale responses from older
 * requests from overwriting newer query results — no manual staleness guard needed.
 */
export function useSearchSuggestions(query: string) {
  const debouncedQuery = useDebouncedValue(query, DEBOUNCE_MS)
  const trimmed = debouncedQuery.trim()

  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEYS.search.suggestions(trimmed),
    queryFn: async () => {
      const params = new URLSearchParams({ q: trimmed, limit: String(SUGGESTION_LIMIT) })
      const result = await swimApi.get<{ offers: { data: OfferResult[] }; streams: StreamResult[] }>(
        `/search?${params.toString()}`,
      )
      if (!result.success) {
        return EMPTY_SUGGESTIONS
      }
      return { offers: result.data.offers.data, streams: result.data.streams }
    },
    enabled: trimmed.length > 0,
  })

  const suggestions = data ?? EMPTY_SUGGESTIONS

  return { suggestions, isLoading }
}
